import Accordion, { AccordionItem } from '@storeblocks/accordion';
import { Button } from '@storeblocks/button';
import { Surface } from '@storeblocks/surface';
import sortBy from 'lodash/sortBy';
import { type FC } from 'react';

import { Fund } from '@/api/funds/models';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { Documents } from '@/components/FundDocuments/Documents';
import { Column, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { FundPerformance } from './FundPerformance';
import { toUrlFriendlySlug } from './utils/toUrlFriendlySlug';

interface Props {
  funds: Fund[];
  'data-test': string;

  /**
   * Wether or not the organization has holdings in the specified funds.
   */
  hasHoldingsInFunds: boolean;
}

export const FundListAccordion: FC<Props> = ({
  funds,
  'data-test': dataTest,
  hasHoldingsInFunds,
}) => {
  const texts = useTexts();
  const organizationCmId = useOrganizationCmId();
  const sortedFunds = sortBy(funds, (fund) => fund.name.toLowerCase());
  const getTrackId = useGetTrackId();

  return (
    <Surface isBackground>
      <Accordion data-test={dataTest} headingsAriaLevel={3}>
        {sortedFunds.map((fund) => (
          <AccordionItem
            key={fund.isin}
            id={fund.isin}
            header={fund.name}
            data-test={`fund-information-${fund.isin}`}
            buttonProps={{
              'data-trackid': `${getTrackId(
                'toggle-fund-information',
                TrackingElement.Button,
              )}`,
              'data-trackid-isin': fund.isin,
              'data-trackid-fund-name': fund.name,
              'data-trackid-has-holdings-in-fund':
                hasHoldingsInFunds.toString(),
            }}
          >
            <WithGap gap="16">
              <Row>
                {/* Left column. */}
                <Column $span={6}>
                  <Documents isin={fund.isin} headingsAriaLevel={4} />
                </Column>

                {/* Right column. */}
                <Column $span={6}>
                  <FundPerformance
                    fundStartDate={fund.startDate}
                    performanceNumbers={fund.performanceNumbers}
                  />
                </Column>
              </Row>
              <Button
                as={InternalLink}
                to={`/${organizationCmId}/sustainability/esg/fund/${
                  fund.isin
                }/${toUrlFriendlySlug(fund.name)}`}
                iconRight="arrow_right"
                variant="tertiary"
                data-trackid={`${getTrackId(
                  'go-to-fund-esg-page',
                  TrackingElement.Link,
                )}`}
                data-trackid-isin={fund.isin}
                data-trackid-fund-name={fund.name}
                data-trackid-source="fund-list-page"
              >
                {texts.pages.fundEsg.linkToPage.title}
              </Button>
            </WithGap>
          </AccordionItem>
        ))}
      </Accordion>
    </Surface>
  );
};
